// Hook gom toàn bộ state + gọi API cho trang danh sách bài đăng kênh cộng
// đồng: tab "Bài đăng" (active, ghim/sửa/xóa) + tab "Chờ duyệt" (pending,
// duyệt/từ chối). Tách khỏi community-post-list.js để giữ file UI < 200 dòng.
import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import communityApi from "../../../api/communityApi";
import { EMPTY_COMMUNITY_POST_FILTERS } from "./community-post-filter-bar";

/** Bộ lọc UI -> query API (khoảng ngày dayjs -> from/to ms, trọn ngày). */
function toQueryFilters({ range, ...rest }) {
    return {
        ...rest,
        from: range?.[0] ? range[0].startOf("day").valueOf() : undefined,
        to: range?.[1] ? range[1].endOf("day").valueOf() : undefined,
    };
}

export function useCommunityPostModeration() {
    const [view, setView] = useState("active"); // 'active' | 'pending'
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [pendingPosts, setPendingPosts] = useState([]);
    const [pendingPagination, setPendingPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [pendingTotal, setPendingTotal] = useState(0); // badge số bài chờ duyệt
    const [rejectPostId, setRejectPostId] = useState(null); // postId đang mở modal nhập lý do từ chối
    const [filters, setFiltersState] = useState(EMPTY_COMMUNITY_POST_FILTERS);
    // Ref để mọi lời gọi fetchPosts (đổi trang, sau khi ghim/xoá/sửa) giữ nguyên bộ lọc
    const filtersRef = useRef(EMPTY_COMMUNITY_POST_FILTERS);
    const [hideDays, setHideDays] = useState(0); // cấu hình tự ẩn bài (0 = tắt)

    const fetchPosts = useCallback(async (page = 1, limit = 20) => {
        try {
            setLoading(true);
            const { data } = await communityApi.getAdminPosts(page, limit, toQueryFilters(filtersRef.current));
            setPosts(data.posts || []);
            setHideDays(data.hideDays || 0);
            setPagination({ current: data.page || page, pageSize: data.limit || limit, total: data.total || 0 });
            setPendingTotal(data.pendingTotal || 0);
        } catch {
            message.error("Không thể tải danh sách bài đăng");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPendingPosts = useCallback(async (page = 1, limit = 20) => {
        try {
            setLoading(true);
            const { data } = await communityApi.getPendingPosts(page, limit);
            setPendingPosts(data.posts || []);
            setPendingPagination({ current: data.page || page, pageSize: data.limit || limit, total: data.total || 0 });
            setPendingTotal(data.total || 0);
        } catch {
            message.error("Không thể tải danh sách bài chờ duyệt");
        } finally {
            setLoading(false);
        }
    }, []);

    // Chuyển sang tab "Chờ duyệt" -> nạp dữ liệu lần đầu
    useEffect(() => {
        if (view === "pending") fetchPendingPosts(1, pendingPagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    // Đổi bộ lọc -> về trang 1
    const setFilters = (next) => {
        filtersRef.current = next;
        setFiltersState(next);
        fetchPosts(1, pagination.pageSize);
    };

    const handleTableChange = (p) => {
        if (view === "pending") fetchPendingPosts(p.current, p.pageSize);
        else fetchPosts(p.current, p.pageSize);
    };

    const handleApprove = async (postId) => {
        try {
            await communityApi.moderatePost(postId, "approve");
            message.success("Đã duyệt bài đăng");
            fetchPendingPosts(pendingPagination.current, pendingPagination.pageSize);
        } catch (error) {
            message.error(error.response?.data?.message || "Không thể duyệt bài đăng");
        }
    };

    const handleRejectSubmit = async (reason) => {
        try {
            await communityApi.moderatePost(rejectPostId, "reject", reason);
            message.success("Đã từ chối bài đăng");
            setRejectPostId(null);
            fetchPendingPosts(pendingPagination.current, pendingPagination.pageSize);
        } catch (error) {
            message.error(error.response?.data?.message || "Không thể từ chối bài đăng");
        }
    };

    const handleDelete = async (postId) => {
        try {
            await communityApi.deletePost(postId);
            message.success("Đã xóa bài đăng");
            fetchPosts(pagination.current, pagination.pageSize);
        } catch (error) {
            message.error(error.response?.data?.message || "Không thể xóa bài đăng");
        }
    };

    const handleTogglePinned = async (record, pinned) => {
        // optimistic update
        setPosts((prev) => prev.map((p) => (p.postId === record.postId ? { ...p, pinned: pinned ? 1 : 0 } : p)));
        try {
            await communityApi.setPinned(record.postId, pinned);
            message.success(pinned ? "Đã ghim bài" : "Đã bỏ ghim");
            fetchPosts(pagination.current, pagination.pageSize);
        } catch (error) {
            setPosts((prev) =>
                prev.map((p) => (p.postId === record.postId ? { ...p, pinned: record.pinned } : p))
            );
            message.error(error.response?.data?.message || "Không thể cập nhật ghim");
        }
    };

    return {
        view,
        setView,
        posts,
        loading,
        pagination,
        pendingPosts,
        pendingPagination,
        pendingTotal,
        rejectPostId,
        setRejectPostId,
        filters,
        setFilters,
        hideDays,
        fetchPosts,
        handleTableChange,
        handleApprove,
        handleRejectSubmit,
        handleDelete,
        handleTogglePinned,
    };
}
